import Head from 'next/head';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import {
  guideImage,
  imageSrcSet,
  imageUrl,
} from '@/components/interior-landing/assets';
import FinalCta from '@/components/interior-landing/FinalCta';
import InteriorFaq from '@/components/interior-landing/InteriorFaq';
import InteriorHero from '@/components/interior-landing/InteriorHero';
import { useStartInterior } from '@/components/interior-landing/kit';
import { roomPath } from '@/components/interior-landing/pages';
import type { InteriorLandingPage } from '@/components/interior-landing/types';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import GuideCards from './GuideCards';
import {
  GUIDE_ROOM_NAMES,
  guidePath,
  GUIDES_PUBLISHED,
  STYLES_PATH,
} from './pages';
import type { StyleGuideProps } from './server';
import { GUIDE_ROOMS, type StyleGuide } from './types';

const SITE = 'https://easy-peasy.ai';
const UPDATED = new Date(`${GUIDES_PUBLISHED}T00:00:00Z`).toLocaleDateString(
  'en-US',
  { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' },
);

/**
 * One interior design style guide: what the style is and where it comes
 * from, its colors and materials, what makes a room look that way, the style
 * on three real rooms with tips for each, mistakes, how it compares with
 * similar styles and questions. Every button opens the AI Interior Designer
 * with the style picked.
 */
export default function StyleGuidePage({
  guide,
  page,
  related,
}: StyleGuideProps) {
  const url = `${SITE}${page.path}`;
  const image = imageUrl(guideImage(guide.slug, 'living-room'), 1200);
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { name: 'Easy-Peasy.AI', item: SITE },
        { name: 'Interior Design Styles', item: `${SITE}${STYLES_PATH}` },
        { name: guide.name, item: url },
      ].map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        ...crumb,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${guide.name} Interior Design`,
      description: guide.seoDescription,
      image,
      datePublished: GUIDES_PUBLISHED,
      dateModified: GUIDES_PUBLISHED,
      mainEntityOfPage: url,
      author: { '@type': 'Organization', name: 'Easy-Peasy.AI', url: SITE },
      publisher: {
        '@type': 'Organization',
        name: 'Easy-Peasy.AI',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE}/images/big-logo.png`,
        },
      },
    },
  ];

  return (
    <main className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo
        templateTitle={guide.seoTitle}
        description={guide.seoDescription}
        image={image}
        type='article'
        date={GUIDES_PUBLISHED}
        structuredData={false}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>

      <InteriorHero page={page} />
      <WhatItIs guide={guide} />
      <PaletteAndMaterials guide={guide} />
      <KeyElements guide={guide} />
      <RoomByRoom guide={guide} page={page} />
      <AvoidAndCompare guide={guide} />
      <InteriorFaq page={page} sharedFaqs={false} />

      {related.length ? (
        <section className='pb-16 pt-4 sm:pb-24'>
          <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
            <div className='flex flex-wrap items-end justify-between gap-x-8 gap-y-4'>
              <SectionHeading size='md' title='More interior design styles' />
              <Link
                href={STYLES_PATH}
                className='inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400'
              >
                See all 10 styles
                <ArrowGlyph />
              </Link>
            </div>
            <GuideCards
              guides={related}
              sizes='(min-width: 640px) 31vw, 94vw'
              className='mt-10 sm:grid-cols-3'
            />
          </div>
        </section>
      ) : null}

      <FinalCta page={page} />
      <Footer />
    </main>
  );
}

function WhatItIs({ guide }: { guide: StyleGuide }) {
  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-x-16 gap-y-8 px-5 sm:px-6 lg:grid-cols-12 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading size='md' title={`What is ${guide.name} style?`} />
          <p className={clsxm('mt-5 text-sm', tone.quiet)}>Updated {UPDATED}</p>
        </div>
        <div
          className={clsxm(
            'max-w-[68ch] space-y-5 text-[17px] leading-8 lg:col-span-8',
            tone.body,
          )}
        >
          {guide.intro.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

function PaletteAndMaterials({ guide }: { guide: StyleGuide }) {
  return (
    <section className='bg-white py-20 dark:bg-[#0f0f18] sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={`The ${guide.name} color palette,`}
          tail='and the materials that go with it.'
        />
        <div className='mt-12 grid gap-x-16 gap-y-14 lg:grid-cols-12'>
          <div className='lg:col-span-7'>
            <ul className='grid grid-cols-3 gap-x-4 gap-y-8 sm:grid-cols-5'>
              {guide.palette.map((color) => (
                <li key={color.hex} className='min-w-0'>
                  <div
                    className='aspect-[4/5] rounded-2xl shadow-[inset_0_0_0_1px_rgba(15,15,40,0.08)]'
                    style={{ backgroundColor: color.hex }}
                  />
                  <p
                    className={clsxm(
                      'mt-3 text-[15px] font-semibold leading-snug',
                      tone.ink,
                    )}
                  >
                    {color.name}
                  </p>
                  <p
                    className={clsxm(
                      'mt-0.5 font-mono text-xs uppercase tracking-wide',
                      tone.quiet,
                    )}
                  >
                    {color.hex}
                  </p>
                  <p className={clsxm('mt-1.5 text-sm leading-5', tone.body)}>
                    {color.use}
                  </p>
                </li>
              ))}
            </ul>
            <p
              className={clsxm(
                'mt-10 max-w-[62ch] text-[17px] leading-8',
                tone.body,
              )}
            >
              {guide.paletteNote}
            </p>
          </div>

          <div className='lg:col-span-5'>
            <h3 className={clsxm('text-xl font-semibold', tone.ink)}>
              Materials and textures
            </h3>
            <ul className='mt-6 flex flex-wrap gap-2.5'>
              {guide.materials.map((material) => (
                <li
                  key={material}
                  className={clsxm(
                    'rounded-full bg-[#f3f2f8] px-4 py-2 text-[15px] font-medium dark:bg-white/[0.06]',
                    tone.ink,
                  )}
                >
                  {material}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function KeyElements({ guide }: { guide: StyleGuide }) {
  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={`What makes a room ${guide.name}.`}
          tail={`${guide.elements.length} things to look for.`}
        />
        <ol className='mt-14 grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
          {guide.elements.map((element, index) => (
            <li
              key={element.title}
              className='border-t border-[#0f0f28]/10 pt-6 dark:border-white/10'
            >
              <span className='text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400'>
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3
                className={clsxm(
                  'mt-3 text-xl font-semibold leading-snug',
                  tone.ink,
                )}
              >
                {element.title}
              </h3>
              <p className={clsxm('mt-2 text-base leading-7', tone.body)}>
                {element.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function RoomByRoom({
  guide,
  page,
}: {
  guide: StyleGuide;
  page: InteriorLandingPage;
}) {
  const start = useStartInterior(page);

  return (
    <section className='bg-white py-20 dark:bg-[#0f0f18] sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={`${guide.name}, room by room.`}
          tail='Real rooms, restyled with AI.'
        />
        <div className='mt-14 space-y-20 sm:space-y-24'>
          {GUIDE_ROOMS.map((slug, index) => {
            const roomName = GUIDE_ROOM_NAMES[slug];
            const roomLower = roomName.toLowerCase();
            const image = guideImage(guide.slug, slug);
            return (
              <article
                key={slug}
                className='grid items-center gap-x-14 gap-y-8 lg:grid-cols-12'
              >
                <figure
                  className={clsxm(
                    'lg:col-span-7',
                    index % 2 === 1 && 'lg:order-2',
                  )}
                >
                  <div
                    className='relative overflow-hidden rounded-[22px] bg-[#e9e9f2] shadow-[0_40px_80px_-48px_rgba(22,12,90,0.55)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-[#1b1b28] dark:ring-white/10'
                    style={{ aspectRatio: `${image.aspect}` }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageUrl(image, 1200)}
                      srcSet={imageSrcSet(image)}
                      sizes='(min-width: 1280px) 700px, (min-width: 1024px) 56vw, 94vw'
                      alt={`A ${roomLower} in ${guide.name} style, made with AI`}
                      loading='lazy'
                      decoding='async'
                      className='absolute inset-0 h-full w-full object-cover'
                    />
                  </div>
                  <figcaption className={clsxm('mt-3 text-sm', tone.quiet)}>
                    Our sample {roomLower}, restyled in {guide.name} by the AI
                    Interior Designer.
                  </figcaption>
                </figure>

                <div className='lg:col-span-5'>
                  <h3
                    className={clsxm(
                      'text-2xl font-semibold leading-tight sm:text-[1.75rem]',
                      tone.ink,
                    )}
                  >
                    {indefinite(guide.name)} {guide.name} {roomLower}
                  </h3>
                  <ul className='mt-6 space-y-4'>
                    {guide.rooms[slug].map((tip) => (
                      <li
                        key={tip}
                        className={clsxm(
                          'flex gap-3 text-[17px] leading-7',
                          tone.body,
                        )}
                      >
                        <span className='mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
                          <CheckGlyph className='h-3 w-3' />
                        </span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                  <div className='mt-8 flex flex-wrap items-center gap-3'>
                    <button
                      type='button'
                      onClick={() =>
                        start({ surface: 'cta', roomType: roomName })
                      }
                      className={buttonPrimary}
                    >
                      Try it on your {roomLower}
                      <ArrowGlyph />
                    </button>
                    <Link href={roomPath(slug)} className={buttonSecondary}>
                      AI {roomName} Design
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AvoidAndCompare({ guide }: { guide: StyleGuide }) {
  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-x-20 gap-y-16 px-5 sm:px-6 lg:grid-cols-2 lg:px-8'>
        <div>
          <SectionHeading size='md' title='Mistakes to avoid' />
          <ul className='mt-8 space-y-5'>
            {guide.avoid.map((mistake) => (
              <li
                key={mistake}
                className={clsxm('flex gap-3 text-[17px] leading-7', tone.body)}
              >
                <span className='mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400'>
                  <CrossGlyph />
                </span>
                {mistake}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <SectionHeading
            size='md'
            title={`${guide.name} and similar styles`}
          />
          <div className='mt-8 space-y-9'>
            {guide.versus.map((other) => (
              <div key={other.name}>
                <h3 className={clsxm('text-lg font-semibold', tone.ink)}>
                  {guide.name} vs. {other.name}
                </h3>
                <p className={clsxm('mt-2 text-[17px] leading-7', tone.body)}>
                  {other.body}
                </p>
                {other.slug ? (
                  <Link
                    href={guidePath(other.slug)}
                    className='mt-3 inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400'
                  >
                    Read the {other.name} guide
                    <ArrowGlyph />
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** "A" or "An" before a style name ("An Art Deco bedroom"). */
const indefinite = (name: string) => (/^[aeiou]/i.test(name) ? 'An' : 'A');

function CrossGlyph() {
  return (
    <svg
      viewBox='0 0 12 12'
      fill='none'
      aria-hidden='true'
      className='h-2.5 w-2.5'
    >
      <path
        d='M2.5 2.5l7 7m0-7l-7 7'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
      />
    </svg>
  );
}
